import { CheckCircle2, BarChart3, LineChart, PieChart, TrendingUp } from "lucide-react";

const KPIS = [
  "Besuchte Haushalte",
  "Geführte Gespräche",
  "Interessentenquote",
  "Abschlussquote",
  "Widerrufsquote",
  "Regionale Hotspots",
  "Rückgewinnungspotenzial",
  "Teamperformance",
  "Tarifinteresse",
  "Kundenfeedback",
];

export default function Dashboard() {
  return (
    <section
      id="dashboard"
      data-testid="dashboard-section"
      className="py-20 md:py-32 bg-[#F9FAFB]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A3B2C] mb-4">
              Transparenz, die Vertrauen schafft.
            </div>
            <h2
              className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] text-[#0A3B2C] mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="dashboard-title"
            >
              Daten. Fakten. Fortschritt.
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
              Mit unserem Dashboard behalten Auftraggeber jederzeit den Überblick
              über alle relevanten Kennzahlen ihrer Kampagne.
            </p>

            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-8">
              {KPIS.map((k, i) => (
                <div
                  key={i}
                  data-testid={`dashboard-kpi-${i}`}
                  className="flex items-center gap-2.5 text-sm text-gray-700"
                >
                  <CheckCircle2 className="h-4 w-4 text-[#10B981] flex-shrink-0" />
                  {k}
                </div>
              ))}
            </div>

            <p className="text-sm text-gray-500 leading-relaxed max-w-xl italic border-l-2 border-[#0A3B2C]/20 pl-4">
              faktor-direkt verbindet persönliche Bürgernähe mit digitaler
              Transparenz. Jeder Kundenkontakt kann dokumentiert, ausgewertet
              und zur Qualitätsverbesserung genutzt werden.
            </p>
          </div>

          {/* Right - Dashboard mockup */}
          <div className="relative">
            <div className="relative rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden" data-testid="dashboard-mockup">
              {/* Browser bar */}
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
                <div className="ml-3 text-xs text-gray-400 font-mono">dashboard.faktor-direkt.de</div>
              </div>

              {/* Header */}
              <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded bg-[#0A3B2C]" />
                  <div className="text-xs font-bold text-[#0A3B2C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    faktor-direkt
                  </div>
                </div>
                <div className="text-[10px] text-gray-400">Kampagne Nord · Live</div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 p-5">
                <StatCard label="Haushalte" value="12.842" trend="+8%" />
                <StatCard label="Gespräche" value="4.715" trend="+12%" />
                <StatCard label="Interess." value="1.892" trend="+6%" />
                <StatCard label="Abschlüsse" value="892" trend="+4%" />
              </div>

              {/* Chart section */}
              <div className="grid grid-cols-2 gap-3 px-5 pb-5">
                <div className="rounded-lg border border-gray-100 p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Gespräche</div>
                    <LineChart className="h-3 w-3 text-[#0A3B2C]" />
                  </div>
                  <svg viewBox="0 0 200 60" className="w-full h-16">
                    <polyline
                      fill="none"
                      stroke="#0A3B2C"
                      strokeWidth="2"
                      points="0,45 25,38 50,42 75,30 100,32 125,20 150,25 175,15 200,10"
                    />
                    <polyline
                      fill="rgba(16, 185, 129, 0.15)"
                      stroke="none"
                      points="0,45 25,38 50,42 75,30 100,32 125,20 150,25 175,15 200,10 200,60 0,60"
                    />
                  </svg>
                </div>
                <div className="rounded-lg border border-gray-100 p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Tarife</div>
                    <PieChart className="h-3 w-3 text-[#0A3B2C]" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="relative h-14 w-14 rounded-full" style={{
                      background: "conic-gradient(#0A3B2C 0 45%, #10B981 45% 70%, #6EE7B7 70% 90%, #E5E7EB 90% 100%)"
                    }}>
                      <div className="absolute inset-2 rounded-full bg-white" />
                    </div>
                    <div className="text-[10px] space-y-1">
                      <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#0A3B2C]" /> Strom</div>
                      <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#10B981]" /> Gas</div>
                      <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#6EE7B7]" /> Solar</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-lg hidden md:flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-[#10B981]" />
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Live</div>
                <div className="text-xs font-bold text-[#0A3B2C]">Kampagne aktiv</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, trend }) {
  return (
    <div className="rounded-lg border border-gray-100 p-3 bg-[#F9FAFB]">
      <div className="text-[9px] uppercase tracking-widest text-gray-400 font-semibold">{label}</div>
      <div className="text-lg font-bold text-[#0A3B2C] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
        {value}
      </div>
      <div className="text-[9px] text-[#10B981] font-semibold mt-0.5">{trend}</div>
    </div>
  );
}
